import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import ShopmyImage from '../General/ShopmyImage';
import { getSortMetric, composeVibeWhy, hasUnmatchedCriteria } from '../../helpers/discover_helpers';
import './DiscoverResult.scss';

const DiscoverResult = ({ creator: c, sortBy, cardRef, showBoundary, hideTopCategory, disambiguation, explainer, proseEnabled }) => {
  const sortMetric = getSortMetric(sortBy, c);
  // "Why this creator surfaced" — per-hit evidence + set-level disambiguation.
  // Empty unless this is a vibe search with evidence attached.
  const whyParts = composeVibeWhy(c.evidence, disambiguation);
  const isDeprioritized = c.evidence?.label === 'deprioritized';

  // Vibe full-matches show prose (shimmer while generating); everything else,
  // and any failure, falls back to the templated line.
  const isFullMatch = !!c.evidence && !hasUnmatchedCriteria(c.evidence);
  const proseDone = explainer?.status === 'done' && !!explainer.text;
  const proseFailed = explainer?.status === 'error';
  const showShimmer = proseEnabled && isFullMatch && whyParts.length > 0 && !proseDone && !proseFailed;
  return (
    <div className='discover-result'>
      {showBoundary && (
        <div className='discover-scored-boundary'>
          <span>Not yet scored for this brand</span>
        </div>
      )}
      <div ref={cardRef} className='creator-card'>
        {c.image
          ? <ShopmyImage source={c.image} alt={c.name} />
          : <div className='creator-avatar-fallback'>{(c.name || c.username || '?')[0].toUpperCase()}</div>
        }
        <div className='creator-meta'>
          <div className='creator-name'>{c.name} <span>@{c.username}</span></div>
          {proseDone ? (
            <div className='creator-why creator-why-revealed'>{explainer.text}</div>
          ) : showShimmer ? (
            <div className='creator-why-shimmer' aria-hidden='true' />
          ) : whyParts.length > 0 ? (
            <div className={cn('creator-why', { deprioritized: isDeprioritized })}>
              {whyParts.join(' · ')}
            </div>
          ) : null}
          {sortMetric && (
            <div className='creator-sort-metric'>
              <span className='label'>{sortMetric.label}:</span>{' '}
              <strong className='value'>{sortMetric.value}</strong>
            </div>
          )}
          <div className='creator-score'>
            Brand Match Score:{' '}
            <strong className={c.categoryAffinity == null ? 'unscored' : ''}>
              {c.categoryAffinity != null
                ? `${(c.categoryAffinity * 100).toFixed(0)}%`
                : 'Not scored yet'}
            </strong>
          </div>
          {!hideTopCategory && c.topCategoryAffinityName && (
            <div className='creator-category'>Top category: {c.topCategoryAffinityName}</div>
          )}
          {c.userBrandSegment && (
            <div className='creator-segment'>Segment: {c.userBrandSegment}</div>
          )}
        </div>
      </div>
    </div>
  );
};

DiscoverResult.propTypes = {
  creator: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string,
    username: PropTypes.string.isRequired,
    image: PropTypes.string,
    categoryAffinity: PropTypes.number,
    topCategoryAffinityName: PropTypes.string,
    userBrandSegment: PropTypes.string,
    evidence: PropTypes.object,
  }).isRequired,
  sortBy: PropTypes.string.isRequired,
  cardRef: PropTypes.func,
  showBoundary: PropTypes.bool,
  hideTopCategory: PropTypes.bool,
  disambiguation: PropTypes.object,
  explainer: PropTypes.shape({
    status: PropTypes.oneOf(['loading', 'done', 'error']),
    text: PropTypes.string,
  }),
  proseEnabled: PropTypes.bool,
};

export default DiscoverResult;
